import React, { useEffect, useState, useMemo, useCallback } from "react";

import { Icon } from "@iconify/react/dist/iconify.js";
import { useTheme } from "@mui/material";

import { TYPE_FOLDER } from "../../util/constants";
import { DocumentData } from "../../util/interfaces/DocumentData";

interface IDocumentsGridSelectedProps {
  selectedRows: string[];
  allRows: DocumentData[];
  callbackSetSelectedRows: (_idsToRemove: string[]) => void;
  onHiddenItemsChange?: (_hiddenIds: string[]) => void;
}
interface HierarchyNode {
  item: DocumentData;
  children: HierarchyNode[];
  level: number;
}

const DocumentsGridSelected: React.FC<IDocumentsGridSelectedProps> = React.memo(
  ({ selectedRows, allRows, callbackSetSelectedRows, onHiddenItemsChange }) => {
    // Only log when selectedRows actually change
    const prevSelectedRowsRef = React.useRef<string[]>([]);
    React.useEffect(() => {
      if (JSON.stringify(selectedRows) !== JSON.stringify(prevSelectedRowsRef.current)) {
        prevSelectedRowsRef.current = selectedRows;
      }
    }, [selectedRows]);

    const [expandedFolders, setExpandedFolders] = useState<string[]>([]);
    const [hiddenItems, setHiddenItems] = useState<string[]>([]);
    const theme = useTheme();

    // Memoize helper functions to prevent re-creation on every render
    const findItemById = useCallback(
      (id: string): DocumentData | undefined => {
        return allRows.find((item) => item.id === id);
      },
      [allRows]
    );

    const getChildren = useCallback(
      (parentId: string): DocumentData[] => {
        return allRows.filter((item) => item.parentId === parentId);
      },
      [allRows]
    );

    // Memoize expensive calculations
    const getAllDescendants = useCallback(
      (parentId: string): string[] => {
        const children = getChildren(parentId);
        const descendants: string[] = children.map((child) => child.id);

        for (const child of children) {
          descendants.push(...getAllDescendants(child.id));
        }

        return descendants;
      },
      [getChildren]
    );

    const getAllAncestors = useCallback(
      (itemId: string): string[] => {
        const item = findItemById(itemId);
        if (!item?.parentId) return [];

        const ancestors = [item.parentId];
        ancestors.push(...getAllAncestors(item.parentId));

        return ancestors;
      },
      [findItemById]
    );

    // Memoize the hierarchy tree construction to avoid expensive recalculations
    const hierarchyTree = useMemo(() => {
      if (selectedRows.length === 0 || allRows.length === 0) {
        return [];
      }

      const itemsToShow = new Set<string>();

      // For each selected ID, get complete hierarchy (up and down)
      for (const id of selectedRows) {
        // Skip if this item is hidden
        if (hiddenItems.includes(id)) continue;

        // Add the selected item itself
        itemsToShow.add(id);

        // Add all ancestors (going up)
        const ancestors = getAllAncestors(id);
        for (const ancestorId of ancestors) {
          if (!hiddenItems.includes(ancestorId)) {
            itemsToShow.add(ancestorId);
          }
        }

        // Add all descendants (going down)
        const descendants = getAllDescendants(id);
        for (const descendantId of descendants) {
          if (!hiddenItems.includes(descendantId)) {
            itemsToShow.add(descendantId);
          }
        }
      }

      // Build the tree structure recursively
      const buildNode = (itemId: string, level: number): HierarchyNode | null => {
        const item = findItemById(itemId);
        if (!item || hiddenItems.includes(itemId)) return null;

        const children: HierarchyNode[] = [];

        // Get all direct children of this item that should be shown
        const directChildren = getChildren(itemId);
        for (const child of directChildren) {
          if (itemsToShow.has(child.id) && !hiddenItems.includes(child.id)) {
            const childNode = buildNode(child.id, level + 1);
            if (childNode) {
              children.push(childNode);
            }
          }
        }

        // Sort children: folders first, then files, then alphabetically
        const sortedChildren = [...children].sort((a: HierarchyNode, b: HierarchyNode) => {
          if (a.item.fileType === TYPE_FOLDER && b.item.fileType !== TYPE_FOLDER) return -1;
          if (a.item.fileType !== TYPE_FOLDER && b.item.fileType === TYPE_FOLDER) return 1;
          return a.item.documentName.localeCompare(b.item.documentName);
        });

        return {
          item,
          children: sortedChildren,
          level,
        };
      };

      // Find root items (items without parents or whose parents are not in the tree)
      const rootItems = Array.from(itemsToShow).filter((id) => {
        const item = findItemById(id);
        return !hiddenItems.includes(id) && (!item?.parentId || !itemsToShow.has(item.parentId));
      });

      // Build tree from root items
      const tree = rootItems
        .map((rootId) => buildNode(rootId, 0))
        .filter((node): node is HierarchyNode => node !== null);

      // Sort root items: folders first, then files, then alphabetically
      const sortedTree = [...tree].sort((a: HierarchyNode, b: HierarchyNode) => {
        if (a.item.fileType === TYPE_FOLDER && b.item.fileType !== TYPE_FOLDER) return -1;
        if (a.item.fileType !== TYPE_FOLDER && b.item.fileType === TYPE_FOLDER) return 1;
        return a.item.documentName.localeCompare(b.item.documentName);
      });

      return sortedTree;
    }, [
      selectedRows,
      allRows,
      hiddenItems,
      findItemById,
      getChildren,
      getAllAncestors,
      getAllDescendants,
    ]);

    // Helper function to check and hide empty parent folders recursively
    const checkAndHideEmptyParents = useCallback(
      (childId: string, itemsToHideLocal: string[], currentHiddenItems: string[]) => {
        const child = findItemById(childId);
        if (!child?.parentId) return;

        const parent = findItemById(child.parentId);
        if (parent?.fileType !== TYPE_FOLDER) return;

        // Get all children of the parent
        const siblings = getChildren(parent.id);

        // Check if all siblings will be hidden after this operation
        const allSiblingsHidden = siblings.every(
          (sibling) =>
            itemsToHideLocal.includes(sibling.id) || currentHiddenItems.includes(sibling.id)
        );

        if (allSiblingsHidden) {
          // Hide the parent folder too
          itemsToHideLocal.push(parent.id);
          // Recursively check the parent's parent
          checkAndHideEmptyParents(parent.id, itemsToHideLocal, currentHiddenItems);
        }
      },
      [findItemById, getChildren]
    );

    // Helper function to collect items to remove from selection
    const collectItemsToRemove = useCallback(
      (itemId: string, item: DocumentData, uniqueItemsToHide: string[]) => {
        const itemsToRemove: string[] = [];

        // If the item itself is directly selected, remove it
        if (selectedRows.includes(itemId)) {
          itemsToRemove.push(itemId);

          // If it's a folder, also remove all its descendants that are selected
          if (item.fileType === TYPE_FOLDER) {
            const descendants = getAllDescendants(itemId);
            for (const descendantId of descendants) {
              if (selectedRows.includes(descendantId)) {
                itemsToRemove.push(descendantId);
              }
            }
          }
        }

        // Also remove any parent folders that became empty and are selected
        for (const hiddenId of uniqueItemsToHide) {
          if (selectedRows.includes(hiddenId)) {
            itemsToRemove.push(hiddenId);
          }
        }

        return itemsToRemove;
      },
      [selectedRows, getAllDescendants]
    );

    // Function to handle item removal with hierarchy logic
    const handleRemoveItem = useCallback(
      (itemId: string) => {
        const item = findItemById(itemId);
        if (!item) return;

        // ALWAYS hide the item immediately for visual feedback
        const itemsToHideLocal: string[] = [itemId];

        // If it's a folder, also hide all its descendants
        if (item.fileType === TYPE_FOLDER) {
          const descendants = getAllDescendants(itemId);
          itemsToHideLocal.push(...descendants);
        }

        // Check if parent folders should also be hidden (if they become empty)
        checkAndHideEmptyParents(itemId, itemsToHideLocal, hiddenItems);

        // Update hidden items first for immediate visual feedback
        const uniqueItemsToHide = [...new Set(itemsToHideLocal)];
        const newHiddenItems = [...hiddenItems, ...uniqueItemsToHide];
        setHiddenItems(newHiddenItems);
        onHiddenItemsChange?.(newHiddenItems);

        // Collect and remove items from selection
        const itemsToRemove = collectItemsToRemove(itemId, item, uniqueItemsToHide);
        const uniqueItemsToRemove = [...new Set(itemsToRemove)];

        if (uniqueItemsToRemove.length > 0) {
          callbackSetSelectedRows(uniqueItemsToRemove);
        }
      },
      [
        hiddenItems,
        findItemById,
        getAllDescendants,
        checkAndHideEmptyParents,
        collectItemsToRemove,
        callbackSetSelectedRows,
        onHiddenItemsChange,
      ]
    );

    // Reset hidden items only when selectedRows change substantially (not just removals)
    // We detect this by checking if new items were added (length increased)
    useEffect(() => {
      const prevLength = prevSelectedRowsRef.current.length;
      const currentLength = selectedRows.length;

      // Only reset hidden items if selection increased or completely changed
      if (currentLength > prevLength) {
        setHiddenItems([]);
        onHiddenItemsChange?.([]);
        return;
      }

      // Check if any previously hidden item is now selected again
      const hiddenItemsNowSelected = hiddenItems.filter((hiddenId) =>
        selectedRows.includes(hiddenId)
      );

      if (hiddenItemsNowSelected.length > 0) {
        // Remove these items from hidden list since they're selected again
        const newHiddenItems = hiddenItems.filter((id) => !hiddenItemsNowSelected.includes(id));
        setHiddenItems(newHiddenItems);
        onHiddenItemsChange?.(newHiddenItems);
        return;
      }

      // Clean up hidden items that are no longer in the hierarchy
      // (their parent folders were deselected)
      const filteredHiddenItems = hiddenItems.filter((hiddenId) => {
        const item = findItemById(hiddenId);
        if (!item) return false;

        // Check if any ancestor is still selected
        const ancestors = getAllAncestors(hiddenId);
        return ancestors.some((ancestorId) => selectedRows.includes(ancestorId));
      });

      if (filteredHiddenItems.length !== hiddenItems.length) {
        setHiddenItems(filteredHiddenItems);
        onHiddenItemsChange?.(filteredHiddenItems);
      }
    }, [selectedRows, hiddenItems, findItemById, getAllAncestors, onHiddenItemsChange]); // Helper functions to handle folder expand/collapse
    const expandFolder = useCallback((itemId: string) => {
      setExpandedFolders((prev) => [...prev, itemId]);
    }, []);

    const collapseFolder = useCallback((itemId: string) => {
      setExpandedFolders((prev) => prev.filter((id) => id !== itemId));
    }, []);

    // Memoize the render function to prevent unnecessary re-renders
    const renderNode = useCallback(
      (node: HierarchyNode): React.ReactNode => {
        const { item, children, level } = node;
        const isExpanded = expandedFolders.includes(item.id);
        const hasChildren = children.length > 0;
        const isDirectlySelected = selectedRows.includes(item.id);

        return (
          <React.Fragment key={item.id}>
            <div
              className="flex space-between align-items-center"
              style={{
                paddingRight: "10px",
                height: "2rem",
                paddingLeft: `${level * 20}px`,
              }}
            >
              <div className="flex gap-8 align-items-center" style={{ width: "90%" }}>
                {/* Expand/Collapse icon for folders with children */}
                {item.fileType === TYPE_FOLDER && hasChildren && (
                  <Icon
                    icon={isExpanded ? "mdi-light:chevron-up" : "mdi-light:chevron-down"}
                    width={20}
                    height={20}
                    color="#1C1B1F"
                    className="cursor-pointer"
                    onClick={() => (isExpanded ? collapseFolder(item.id) : expandFolder(item.id))}
                  />
                )}

                {/* Spacer for items without expand icon */}
                {!(item.fileType === TYPE_FOLDER && hasChildren) && <div style={{ width: 20 }} />}

                {/* File/Folder icon */}
                <Icon
                  icon={
                    item.fileType === TYPE_FOLDER
                      ? "material-symbols-light:folder-outline-rounded"
                      : "mdi:file-outline"
                  }
                  width={20}
                  height={20}
                  color="#1C1B1F"
                />

                {/* File/Folder name */}
                <span
                  style={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    width: "90%",
                    lineBreak: "normal",
                    whiteSpace: "nowrap",
                    opacity: isDirectlySelected ? 1 : 0.6, // Dim items that are not directly selected
                    fontWeight: isDirectlySelected ? "bold" : "normal",
                  }}
                  title={item.documentName}
                >
                  {item.documentName}
                </span>
              </div>

              {/* Remove button - show on ALL items */}
              <Icon
                icon="zondicons:minus-outline"
                className="cursor-pointer"
                style={{ overflow: "visible" }}
                width={13}
                color={theme.palette.primary.main}
                onClick={() => {
                  handleRemoveItem(item.id);
                }}
              />
            </div>

            {/* Render children if expanded */}
            {item.fileType === TYPE_FOLDER && hasChildren && isExpanded && (
              <>{children.map((child) => renderNode(child))}</>
            )}
          </React.Fragment>
        );
      },
      [
        expandedFolders,
        selectedRows,
        theme.palette.primary.main,
        handleRemoveItem,
        expandFolder,
        collapseFolder,
      ]
    );

    return (
      <>
        {hierarchyTree.length > 0 && (
          <div>{hierarchyTree.map((node: HierarchyNode) => renderNode(node))}</div>
        )}
      </>
    );
  }
);

DocumentsGridSelected.displayName = "DocumentsGridSelected";

export default DocumentsGridSelected;
